import { useCallback, useMemo, useRef, useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import AddToListSolid from '@simplywallst/ui-core/icons/AddToListSolid';
import {
  Grid,
  Heading,
  ButtonWithDropdown,
  useTheme,
  Box,
} from '@simplywallst/ui-core';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

import { useWatchlists } from '@/hooks/useWatchlist';
import type { Watchlists } from '@/hooks/useWatchlist';

import { WATCHLISTS_LIMIT } from '../../constants';

import {
  WatchlistDropdownItem,
  WatchlistDropdownSecondaryItem,
  WatchlistSelectOptions,
} from './WatchlistDropdownItem';

const CreateWatchlistModal = loadable(() => import('../CreateWatchlistModal'));
const DeleteWatchlistModal = loadable(() => import('../DeleteWatchlistModal'));
const WatchlistsLimitModal = loadable(() => import('../WatchlistsLimitModal'));

type Watchlist = Watchlists[number];

interface Props {
  watchlist: Watchlist;
  onSelectWatchlist: (watchlistId: Watchlist['id']) => void;
}

type Modal =
  | { type: 'createList' }
  | { type: 'createLimitReached' }
  | { type: 'deleteList'; payload: Watchlist };

export const WatchlistDropdown = ({ watchlist, onSelectWatchlist }: Props) => {
  const theme = useTheme();
  const { data: watchlists } = useWatchlists();
  const { trackEvent } = useTracking();

  const [modal, setModal] = useState<Modal | null>(null);
  const handleCloseModal = useCallback(() => {
    setModal(null);
  }, []);

  const handleSelect = useCallback(
    (value: string, selectedId: string) => {
      const currentSelectedWatchlist = watchlists?.find(
        (watchlist) => watchlist.id === selectedId
      );

      if (value === WatchlistSelectOptions.SELECT_LIST) {
        onSelectWatchlist(selectedId);
      } else if (
        value === WatchlistSelectOptions.DELETE_LIST &&
        currentSelectedWatchlist
      ) {
        setModal({ type: 'deleteList', payload: currentSelectedWatchlist });
      } else if (
        value === WatchlistSelectOptions.CREATE_LIST &&
        watchlists &&
        watchlists.length >= WATCHLISTS_LIMIT
      ) {
        setModal({ type: 'createLimitReached' });
      } else if (value === WatchlistSelectOptions.CREATE_LIST) {
        setModal({ type: 'createList' });
      }
    },
    [onSelectWatchlist, watchlists]
  );

  const options = useMemo(() => {
    if (typeof watchlists === 'undefined') {
      return [];
    }
    return watchlists.map((option) => (
      <WatchlistDropdownItem
        data-cy-id={`dropdown-item-${option.id}`}
        key={`dropdown-item-${option.id}`}
        option={option}
        isSelected={option.id === watchlist.id}
      />
    ));
  }, [watchlists, watchlist.id]);

  const { current: secondaryOptions } = useRef([
    <WatchlistDropdownSecondaryItem
      data-cy-id="dropdown-item-create-new-list"
      key="dropdown-item-create-new-list"
      option={{
        label: 'Create new list',
        value: WatchlistSelectOptions.CREATE_LIST,
        icon: <AddToListSolid />,
      }}
      isSelected={false}
    />,
  ]);

  return (
    <Box
      position="relative"
      marginTop="x2"
      marginBottom="x2"
      transform="translateX(-12px)"
      zIndex={2}
    >
      <ButtonWithDropdown
        floatLeft={true}
        styleType="passiveLarge"
        data-cy-id="watchlist-dropdown"
        onSelect={handleSelect}
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'watchlistDropdown',
          });
        }}
        options={options}
        secondaryOptions={secondaryOptions}
      >
        <Grid
          alignItems="center"
          gap="8px"
          templateColumns="fit-content(120px) 24px"
          templateColumnsSm="fit-content(420px) 24px"
        >
          <Heading as="h1" typography="megaStrong" isTruncated>
            {watchlist.name}
          </Heading>
          <ChevronDownSmallSolid fill={theme.color.whiteRGBA(0.5)} />
        </Grid>
      </ButtonWithDropdown>

      {modal?.type === 'createLimitReached' && (
        <WatchlistsLimitModal
          onClose={handleCloseModal}
          watchlistsLimit={WATCHLISTS_LIMIT}
        />
      )}

      {modal?.type === 'createList' && (
        <CreateWatchlistModal
          onClose={handleCloseModal}
          onCreate={handleCloseModal}
        />
      )}

      {modal?.type === 'deleteList' && modal.payload && (
        <DeleteWatchlistModal
          watchlist={modal.payload}
          onClose={handleCloseModal}
          onDelete={handleCloseModal}
        />
      )}
    </Box>
  );
};

export default WatchlistDropdown;
